import { db } from './firebaseClient';
import { storage } from './firebaseClient';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  runTransaction,
  writeBatch,
  arrayUnion,
  updateDoc,
} from 'firebase/firestore';

/**
 * Generate a sequential memberID: CRYPTERA_1000, CRYPTERA_1001, etc.
 * Uses Firestore transaction to ensure no duplicate IDs
 */
export const generateMemberID = async () => {
  const counterRef = doc(db, 'counters', 'memberIdCounter');
  
  try {
    const newMemberId = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let currentCount = 1000; // Starting number
      
      if (counterDoc.exists()) {
        currentCount = counterDoc.data().count;
      }
      
      // Increment the counter
      const nextCount = currentCount + 1;
      transaction.set(counterRef, { count: nextCount });
      
      return `CRYPTERA_${currentCount}`;
    });
    
    return newMemberId;
  } catch (error) {
    console.error('Error generating member ID:', error);
    throw error;
  }
};

/**
 * Create a new user in Firestore with all necessary information
 * @param {string} uid - Firebase Auth UID
 * @param {string} email - User email
 * @param {string} name - User full name
 * @param {string} phone - User phone number
 * @param {string} college - User college/institution
 * @returns {Promise<Object>} - User document data with generated memberID
 */
export const createUserInDatabase = async (uid, email, name = '', phone = '', college = '') => {
  try {
    // Generate sequential member ID
    const memberID = await generateMemberID();
    
    const userData = {
      uid: uid,
      email: email,
      name: name,
      phone: phone,
      college: college,
      memberID: memberID,
      pay_status_tech_only: false,
      pay_status_tech_only_id: '',
      pay_status_both: false,
      pay_status_both_id: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      registeredEvents: [],
      profileComplete: !!(name && phone && college),
      editCount: 0
    };

    // Save to users collection
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, userData, { merge: true });

    return userData;
  } catch (error) {
    console.error('Error creating user in database:', error);
    throw error;
  }
};

/**
 * Get user profile by UID
 */
export const getUserProfile = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Validate user before event registration
 * Checks: 1) Profile status (active/inactive)
 * 2) Payment status (if active, check payment)
 * @param {string} uid - User UID
 * @returns {Object} { isValid: boolean, status: string, message: string }
 */
export const validateUserForEventRegistration = async (uid) => {
  try {
    const userProfile = await getUserProfile(uid);
    
    if (!userProfile) {
      return {
        isValid: false,
        status: 'NOT_FOUND',
        message: 'User profile not found'
      };
    }

    // Check if profile is complete and status is active
    if (userProfile.status === 'inactive' || userProfile.status === '') {
      return {
        isValid: false,
        status: 'INCOMPLETE_PROFILE',
        message: 'Please complete your profile before registering for events',
        profileData: userProfile
      };
    }

    // If status is active, check payment status
    if (userProfile.status === 'active') {
      // Check if user has paid and payment is verified
      if (!userProfile.pay_status) {
        return {
          isValid: false,
          status: 'PAYMENT_REQUIRED',
          message: 'Payment of ₹250 is required to register for events',
          profileData: userProfile
        };
      }

      // Check if payment is submitted but not verified
      if (userProfile.pay_status && !userProfile.paymentVerified) {
        return {
          isValid: false,
          status: 'PAYMENT_PENDING',
          message: 'Payment verification is pending. Please wait up to 24 hours.',
          profileData: userProfile
        };
      }

      // User is valid and payment is verified
      return {
        isValid: true,
        status: 'VALID',
        message: 'User is valid to register for events',
        profileData: userProfile
      };
    }

    return {
      isValid: false,
      status: 'UNKNOWN',
      message: 'Unable to validate user status',
      profileData: userProfile
    };
  } catch (error) {
    console.error('Error validating user for event registration:', error);
    throw error;
  }
};

/**
 * Update user payment status with transaction ID and payment proof
 * @param {string} uid - User UID
 * @param {string} transactionId - Payment transaction ID
 * @param {File} paymentProof - Payment proof file
 * @returns {Promise<Object>} Updated user data
 */
export const updatePaymentStatus = async (uid, transactionId, paymentProof = null) => {
  try {
    // Get user profile to get cryptera ID
    const userProfile = await getUserProfile(uid);
    if (!userProfile) {
      throw new Error('User profile not found');
    }
    
    const crypteraId = userProfile.memberID;
    
    // Force create transaction record to test collection creation
    if (crypteraId) {
      let imageBlob = null;
      
      if (paymentProof) {
        const arrayBuffer = await paymentProof.arrayBuffer();
        imageBlob = new Uint8Array(arrayBuffer);
      }
      
      // Create transaction document
      const transactionRef = doc(db, 'transactions', `${crypteraId}_${transactionId}`);
      const transactionData = {
        crypteraId: crypteraId,
        transactionId: transactionId,
        imageData: imageBlob,
        createdAt: new Date().toISOString(),
        status: 'pending'
      };
      
      await setDoc(transactionRef, transactionData);
      
      // Verify the document was created
      const verifyDoc = await getDoc(transactionRef);
      if (!verifyDoc.exists()) {
        console.error('Transaction document was not created!');
      }
    }

    const updateData = {
      pay_status: transactionId,
      paymentVerified: false,
      paymentSubmittedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, updateData, { merge: true });

    return updateData;
  } catch (error) {
    console.error('Detailed payment error:', error);
    throw error;
  }
};

/**
 * Register user for an event
 * Prevents duplicate registrations by checking if user is already registered
 * @param {string} uid - User UID
 * @param {string} eventId - Event ID
 * @param {string} eventName - Event name
 * @returns {Object} Registration data or throws error if already registered
 */
export const registerUserForEvent = async (uid, eventId, eventName) => {
  try {
    // SECURITY CHECK: Verify if user is already registered for this event
    const registrationRef = doc(db, 'event_registrations', `${uid}_${eventId}`);
    const existingRegistration = await getDoc(registrationRef);
    
    if (existingRegistration.exists()) {
      // User is already registered - throw error to prevent duplicate
      throw new Error('ALREADY_REGISTERED');
    }

    const registrationData = {
      uid: uid,
      eventId: eventId,
      eventName: eventName,
      registeredAt: new Date().toISOString(),
      status: 'confirmed',
    };

    // Add to event_registrations collection
    const batch = writeBatch(db);
    
    // Add registration document with composite ID (uid_eventId) for uniqueness
    batch.set(registrationRef, registrationData);

    // Update user's registeredEvents array
    const userRef = doc(db, 'users', uid);
    batch.update(userRef, {
      registeredEvents: arrayUnion({
        eventId: eventId,
        eventName: eventName,
        registeredAt: new Date().toISOString(),
      })
    });

    await batch.commit();
    return registrationData;
  } catch (error) {
    console.error('Error registering for event:', error);
    throw error;
  }
};

/**
 * Get all registrations for a user
 */
export const getUserRegistrations = async (uid) => {
  try {
    const q = query(
      collection(db, 'event_registrations'),
      where('uid', '==', uid)
    );
    
    const querySnapshot = await getDocs(q);
    const registrations = [];
    
    querySnapshot.forEach((doc) => {
      registrations.push(doc.data());
    });
    
    return registrations;
  } catch (error) {
    console.error('Error fetching user registrations:', error);
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (uid, updates) => {
  try {
    const userRef = doc(db, 'users', uid);
    
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    await setDoc(userRef, updateData, { merge: true });
    
    return updateData;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Firestore Database Structure Documentation:
 * 
 * Collection: users
 * Document ID: Firebase UID
 * Fields:
 * - uid: string
 * - email: string
 * - name: string
 * - phone: string
 * - college: string
 * - memberID: string (CRYPTERA_1000, CRYPTERA_1001, etc.)
 * - pay_status_tech_only: bool (default: false)
 * - pay_status_tech_only_id: string (default: "")
 * - pay_status_both: bool (default: false)
 * - pay_status_both_id: string (default: "")
 * - registeredEvents: array (array of event registration objects)
 * - profileComplete: boolean
 * - createdAt: string (ISO timestamp)
 * - updatedAt: string (ISO timestamp)
 * 
 * Collection: event_registrations
 * Document ID: {uid}_{eventId}
 * Fields:
 * - uid: string
 * - eventId: string
 * - eventName: string
 * - registeredAt: string (ISO timestamp)
 * - status: string (confirmed, pending, cancelled)
 * 
 * Collection: counters
 * Document ID: memberIdCounter
 * Fields:
 * - count: number (starts at 1000)
 * 
 * Collection: transactions
 * Document ID: {crypteraId}_{transactionId}
 * Fields:
 * - crypteraId: string (CRYPTERA_1000, etc.)
 * - transactionId: string
 * - imageData: Uint8Array (blob data)
 * - createdAt: string (ISO timestamp)
 * 
 * Note: A separate `profiles` collection is no longer used;
 * all profile data now lives only in the `users` collection.
 */